import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { RefreshCcw, RotateCcw, RotateCw, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ASPECT_PRESETS = [
  { id: 'original', label: 'Original', ratio: null },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '4:5', label: '4:5', ratio: 4 / 5 },
  { id: '16:9', label: '16:9', ratio: 16 / 9 },
  { id: '1.91:1', label: '1.91:1', ratio: 1.91 },
];

const SAFE_FALLBACK_ASPECT = 1;

/**
 * Resolve an authenticated PostImage content/original path against REACT_APP_BACKEND_URL.
 * Matches api.js: configured value is the backend origin without `/api`.
 */
export function resolvePostImageUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const configured = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  if (!configured || configured === 'undefined') return path;
  if (path.startsWith('/')) return `${configured}${path}`;
  return `${configured}/api/${path}`;
}

function useAuthedImageUrl(path) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    setUrl(null);
    setError(null);
    const resolved = resolvePostImageUrl(path);
    if (!resolved) return undefined;

    (async () => {
      try {
        const token = localStorage.getItem('mayr_ops_token');
        const response = await fetch(resolved, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error('Could not load the original picture');
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(objectUrl);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load the original picture');
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return { url, error };
}

function recipeFromImage(image) {
  const t = image?.transform;
  if (!t || typeof t !== 'object') {
    return {
      crop: { x: 0, y: 0 },
      zoom: 1,
      rotation: 0,
      aspectKey: 'original',
      aspectRatio: null,
    };
  }
  const preset = ASPECT_PRESETS.some((item) => item.id === t.aspect_preset)
    ? t.aspect_preset
    : 'original';
  return {
    crop: {
      x: typeof t.crop?.x === 'number' ? t.crop.x : 0,
      y: typeof t.crop?.y === 'number' ? t.crop.y : 0,
    },
    zoom: typeof t.zoom === 'number' ? t.zoom : 1,
    rotation: typeof t.rotation === 'number' ? t.rotation : 0,
    aspectKey: preset,
    aspectRatio: typeof t.aspect_ratio === 'number' ? t.aspect_ratio : null,
  };
}

export default function MediaEditorModal({ image, onClose, onSaved }) {
  const initial = recipeFromImage(image);
  const [crop, setCrop] = useState(initial.crop);
  const [zoom, setZoom] = useState(initial.zoom);
  const [rotation, setRotation] = useState(initial.rotation);
  const [aspectKey, setAspectKey] = useState(initial.aspectKey);
  const [originalAspect, setOriginalAspect] = useState(
    initial.aspectKey === 'original' && typeof initial.aspectRatio === 'number'
      ? initial.aspectRatio
      : null,
  );
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(
    image?.transform?.crop_pixels || null,
  );
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const [altText, setAltText] = useState(image?.alt_text || '');

  const originalPath = image?.original_url || (image?.id ? `/api/post-images/${image.id}/original` : null);
  const { url: blobSrc, error: loadError } = useAuthedImageUrl(originalPath);
  const busy = saving || resetting;

  const fixedRatio = ASPECT_PRESETS.find((item) => item.id === aspectKey)?.ratio;
  const aspect = aspectKey === 'original'
    ? (originalAspect
      || (typeof image?.transform?.aspect_ratio === 'number' ? image.transform.aspect_ratio : null)
      || SAFE_FALLBACK_ASPECT)
    : (fixedRatio ?? SAFE_FALLBACK_ASPECT);

  useEffect(() => {
    const next = recipeFromImage(image);
    setCrop(next.crop);
    setZoom(next.zoom);
    setRotation(next.rotation);
    setAspectKey(next.aspectKey);
    setOriginalAspect(
      next.aspectKey === 'original' && typeof next.aspectRatio === 'number'
        ? next.aspectRatio
        : null,
    );
    setCroppedAreaPixels(image?.transform?.crop_pixels || null);
    setAltText(image?.alt_text || '');
    setError(null);
    // Sync editor controls when the selected image or saved recipe changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- image object identity churns; use stable fields
  }, [image?.id, image?.transform, image?.alt_text, image?.original_url]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const onMediaLoaded = useCallback((mediaSize) => {
    const width = mediaSize?.naturalWidth || mediaSize?.width;
    const height = mediaSize?.naturalHeight || mediaSize?.height;
    if (width > 0 && height > 0) {
      setOriginalAspect(width / height);
    }
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels?.width || !croppedAreaPixels?.height || busy) return;
    setSaving(true);
    setError(null);
    try {
      const preset = ASPECT_PRESETS.find((item) => item.id === aspectKey);
      const resolvedAspect = aspectKey === 'original'
        ? (originalAspect || aspect)
        : (preset?.ratio ?? aspect);
      const recipe = {
        crop: { x: crop.x, y: crop.y },
        crop_pixels: {
          x: Math.round(croppedAreaPixels.x),
          y: Math.round(croppedAreaPixels.y),
          width: Math.round(croppedAreaPixels.width),
          height: Math.round(croppedAreaPixels.height),
        },
        zoom,
        rotation,
        aspect_ratio: resolvedAspect,
        aspect_preset: aspectKey,
        output_width: Math.round(croppedAreaPixels.width),
        output_height: Math.round(croppedAreaPixels.height),
        version: 1,
      };
      const { data } = await api.patch(`/post-images/${image.id}/transform`, recipe);
      onSaved(data);

      if ((altText || '') !== (image.alt_text || '')) {
        try {
          const altResponse = await api.patch(`/post-images/${image.id}`, {
            alt_text: altText.trim() || null,
          });
          onSaved({ ...data, alt_text: altResponse.data.alt_text });
          toast.success('Picture updated');
          onClose();
        } catch {
          const detail = 'Picture adjusted, but the description could not be saved.';
          setError(detail);
          toast.error(detail);
        }
      } else {
        toast.success('Picture updated');
        onClose();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not save picture adjustments';
      setError(detail);
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (busy) return;
    setResetting(true);
    setError(null);
    try {
      const { data } = await api.post(`/post-images/${image.id}/reset-transform`);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setAspectKey('original');
      setCroppedAreaPixels(null);
      onSaved(data);
      toast.success('Picture reset to original');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not reset the picture';
      setError(detail);
      toast.error(detail);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-background/80 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] sm:pb-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-editor-title"
      data-testid="media-editor-modal"
      onKeyDown={(event) => event.key === 'Escape' && !busy && onClose()}
    >
      <div
        className="glass rounded-t-2xl sm:rounded-xl border border-foreground/10 w-full max-w-4xl max-h-[calc(100dvh-env(safe-area-inset-top))] sm:max-h-[95vh] flex flex-col shadow-2xl my-auto sm:my-0 min-h-0 overflow-hidden pt-[env(safe-area-inset-top)] sm:pt-0"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-3 border-b border-foreground/5 flex-shrink-0">
          <h2 id="media-editor-title" className="font-heading font-bold text-foreground text-sm sm:text-base min-w-0 pr-2">
            Adjust picture
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-w-11 min-h-11 shrink-0 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 touch-manipulation"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto min-h-0 overscroll-y-contain touch-pan-y">
            <div className="p-3 sm:p-4 pb-5 grid grid-cols-1 lg:grid-cols-3 gap-4 min-w-0 max-w-full">
              <div className="lg:col-span-2 space-y-3 min-w-0">
                <div className="relative w-full min-h-[176px] h-[min(20rem,max(11rem,34svh))] landscape:min-h-[152px] landscape:h-[min(18rem,max(9.5rem,42dvh))] sm:h-80 sm:min-h-[16rem] sm:landscape:h-80 bg-black/80 rounded-lg overflow-hidden touch-none max-w-full">
                  {!blobSrc && !loadError && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                      Loading original…
                    </div>
                  )}
                  {loadError && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-destructive px-4 text-center">
                      {loadError}
                    </div>
                  )}
                  {blobSrc && (
                    <Cropper
                      image={blobSrc}
                      crop={crop}
                      zoom={zoom}
                      rotation={rotation}
                      aspect={aspect}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      onMediaLoaded={onMediaLoaded}
                      showGrid
                      objectFit="contain"
                    />
                  )}
                </div>
              </div>

              <div className="space-y-4 text-sm min-w-0">
                <section aria-label="Aspect ratio">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Aspect ratio</div>
                  <div className="flex flex-wrap gap-2">
                    {ASPECT_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => setAspectKey(preset.id)}
                        aria-pressed={aspectKey === preset.id}
                        className={`min-h-10 px-3 py-2 rounded-lg text-xs border transition-colors touch-manipulation flex-1 min-w-[4.5rem] sm:flex-initial sm:min-w-0 sm:px-2.5 sm:py-1 ${
                          aspectKey === preset.id
                            ? 'bg-primary/15 border-primary/30 text-primary'
                            : 'border-foreground/10 text-muted-foreground hover:border-foreground/20'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </section>

                <section aria-label="Zoom">
                  <label htmlFor="media-editor-zoom" className="text-xs font-medium text-muted-foreground block mb-1">
                    Zoom
                  </label>
                  <input
                    id="media-editor-zoom"
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(event) => setZoom(Number(event.target.value))}
                    className="w-full min-h-11 py-2 accent-primary touch-manipulation"
                  />
                </section>

                <section aria-label="Rotate" className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setRotation((value) => value - 90)}
                    className="inline-flex items-center justify-center gap-2 min-h-11 px-3 py-2 rounded-lg border border-foreground/10 text-xs text-foreground hover:bg-secondary/50 touch-manipulation w-full sm:w-auto"
                  >
                    <RotateCcw className="w-4 h-4 shrink-0" /> -90°
                  </button>
                  <button
                    type="button"
                    onClick={() => setRotation((value) => value + 90)}
                    className="inline-flex items-center justify-center gap-2 min-h-11 px-3 py-2 rounded-lg border border-foreground/10 text-xs text-foreground hover:bg-secondary/50 touch-manipulation w-full sm:w-auto"
                  >
                    <RotateCw className="w-4 h-4 shrink-0" /> +90°
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={busy}
                    className="inline-flex items-center justify-center gap-2 min-h-11 px-3 py-2 rounded-lg border border-foreground/10 text-xs text-muted-foreground hover:text-foreground touch-manipulation w-full sm:w-auto disabled:opacity-50"
                  >
                    <RefreshCcw className="w-4 h-4 shrink-0" /> {resetting ? 'Resetting…' : 'Reset'}
                  </button>
                </section>

                <section aria-label="Alt text">
                  <label htmlFor="media-editor-alt-text" className="text-xs font-medium text-muted-foreground block mb-1">
                    Alt text
                  </label>
                  <textarea
                    id="media-editor-alt-text"
                    value={altText}
                    onChange={(event) => setAltText(event.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder="Describe this picture (optional)"
                    className="w-full max-w-full box-border bg-secondary/30 border border-foreground/10 focus:border-primary/50 rounded-lg px-3 py-2 text-sm sm:text-xs text-foreground placeholder:text-muted-foreground outline-none resize-y min-h-[5rem]"
                  />
                </section>

                {(error || loadError) && (
                  <p className="text-xs text-destructive" role="alert" data-testid="media-editor-error">
                    {error || loadError}
                  </p>
                )}

                <p className="text-[10px] text-muted-foreground leading-relaxed pb-1">
                  Edits are non-destructive. The original picture stays intact; Preview and publish use the adjusted version.
                </p>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 px-3 sm:px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end border-t border-foreground/5 bg-background shadow-[0_-4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.25)]">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="min-h-12 px-4 py-3 rounded-lg border border-foreground/10 text-muted-foreground text-sm touch-manipulation w-full sm:w-auto sm:min-h-10 sm:py-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !croppedAreaPixels?.width}
              data-testid="media-editor-save"
              className="min-h-12 px-5 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 touch-manipulation w-full sm:w-auto sm:min-h-10 sm:py-2"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
